/**
 * ThemeIcon — Renders an icon for each theme
 * Uses Lucide icons mapped to specific themes in dynamicColors.js
 */

import { 
  Wheat, GraduationCap, Heart, Ribbon, Building, Apple, 
  Droplets, Palette, Home, MoreHorizontal, Landmark, 
  HeartPulse, Utensils, Building2
} from "lucide-react";
import { getThemeIconName, getThemeDotColor } from "../utils/dynamicColors";

// Map icon names to Lucide components
const ICON_MAP = {
  Wheat: Wheat,
  GraduationCap: GraduationCap,
  Heart: Heart,
  HeartPulse: HeartPulse,
  Ribbon: Ribbon,
  Building: Building,
  Building2: Building2,
  Landmark: Landmark,
  Apple: Apple,
  Utensils: Utensils,
  Droplets: Droplets,
  Palette: Palette,
  Home: Home,
  MoreHorizontal: MoreHorizontal,
};

/**
 * ThemeIcon - Renders the icon for a theme with its color
 * @param {string} theme - The theme name
 * @param {number} size - Icon size (default 16)
 * @param {string} className - Additional CSS classes
 * @param {boolean} withBackground - Whether to show colored background (default true)
 */
export default function ThemeIcon({ theme, size = 16, className = "", withBackground = true }) {
  const iconName = getThemeIconName(theme);
  const IconComponent = ICON_MAP[iconName] || MoreHorizontal;
  const bgColor = getThemeDotColor(theme);
  
  if (withBackground) {
    return (
      <div 
        className={`flex items-center justify-center rounded-lg ${className}`}
        style={{ 
          backgroundColor: bgColor,
          width: size * 2,
          height: size * 2,
        }}
      >
        <IconComponent size={size} className="text-white" strokeWidth={2} />
      </div>
    );
  }
  
  return (
    <IconComponent 
      size={size} 
      className={className}
      style={{ color: bgColor }}
      strokeWidth={2}
    />
  );
}

/**
 * ThemeIconBadge - Icon with text label for larger displays
 */
export function ThemeIconBadge({ theme, size = 14 }) {
  const iconName = getThemeIconName(theme);
  const IconComponent = ICON_MAP[iconName] || MoreHorizontal;
  const bgColor = getThemeDotColor(theme);
  
  return (
    <div 
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg"
      style={{ backgroundColor: `${bgColor}20` }}
    >
      <IconComponent size={size} style={{ color: bgColor }} strokeWidth={2} />
      <span className="text-xs font-medium" style={{ color: bgColor }}>{theme}</span>
    </div>
  );
}
